/**
 * Ascend invoices — the Invoice section (OR-1449).
 *
 * Billing for bound policies lives in Ascend, a separate financial-ops
 * platform. These routes bring creation and tracking into Orion.
 *
 *   POST   /v2/invoices/                      → 202; `submission_id` in the BODY
 *   GET    /v2/invoices/?submission_id=…      → { total, items[] }
 *   PUT    /v2/submissions/:id/invoice-draft  → save the in-progress form
 *   GET    /v2/submissions/:id/invoice-draft  → 404 when none
 *   DELETE /v2/submissions/:id/invoice-draft  → discard (404 when none)
 *   GET  /v2/invoices/:invoiceId/files/       → the generated document
 *
 * Mirrors `src/api/v2/invoice/*` from orion-backend — **PR #355, MERGED
 * sep-08**, so this is the settled contract. Invoices are a flat top-level
 * resource (an invoice outlives its submission conceptually); only the
 * draft stays nested under the submission, since it IS per-submission
 * state.
 *
 * The candidates/link/refresh account routes are GONE (sep-04): Ascend has
 * no account search API and every existing Ascend account already matched
 * an Orion brokerage, so discovery was solving a non-problem. The backend
 * now check-then-creates the Account itself; the FE just supplies
 * `account_payload` on a brokerage's first invoice.
 *
 * A saved draft is an `ascend_invoice` ROW with `sync_status="draft"`, so
 * it shows up in the list like any other invoice — that is where the
 * table's Draft badge comes from. Generate finalizes it in place rather
 * than inserting a second row.
 *
 * No trailing slash on the invoice routes: the backend declares them
 * without one under a `/submissions/{id}` prefix, so there is no
 * collection path to redirect. The nested file router DOES use one
 * (`/files/`) or FastAPI 307s.
 *
 * Backend PR #383 (merged sep-11) closed most of the original gap list:
 * the money rollups now ride on every row, `ascend_program_url` is the
 * Ascend deep link, and `DELETE /v2/invoices/:id` archives.
 *
 * Still absent: **un-archive** (archiving is one-way, so the design's
 * Restore has nothing to call) and any write path for premium/fees. The
 * three commission money fields ARE writable, via the draft PUT and
 * `PATCH /v2/invoices/:id`, but sending them means the FE also owns
 * `total_commission_amount`, which the backend stores verbatim instead of
 * deriving — unsettled, so nothing here writes them yet.
 */
import { ApiError, getJsonReal, sendJsonReal } from "../client";

function draftBase(submissionId: string): string {
    return `/v2/submissions/${encodeURIComponent(submissionId)}/invoice-draft`;
}

// ───────────────────────────── Enums ──────────────────────────────────────

/** Which Ascend coverage line the invoice bills under. */
export type AscendCoverageType = "professional_liability" | "excess_liability";

/**
 * Lifecycle of the async Ascend sync job — NOT the invoice's billing
 * status. `complete` only means Orion finished talking to Ascend, and
 * `draft` means the row is a saved form that has never been sent at all.
 */
export type AscendSyncStatus = "draft" | "pending" | "running" | "complete" | "failed";

export const COVERAGE_TYPE_LABELS: Record<AscendCoverageType, string> = {
    professional_liability: "Professional Liability",
    excess_liability: "Excess Liability",
};

// ───────────────────────────── Invoices ───────────────────────────────────

/** Row from `GET .../ascend-invoices`. Decimals arrive as strings. */
interface AscendInvoiceWire {
    id: string;
    submission_id: string;
    sync_status: AscendSyncStatus;
    invoice_sequence: number;
    invoice_number: string;
    due_date: string | null;
    created_at: string;
    updated_at: string;
    coverage_type: AscendCoverageType;
    /**
     * Ascend's own billing status, captured ONCE when the sync completes.
     * There is no webhook or polling refreshing it afterwards, so treat it
     * as a snapshot, never as live state.
     */
    ascend_invoice_status: string | null;
    ascend_total_amount_cents: number | null;
    fni: string | null;
    effective_date: string | null;
    expiration_date: string | null;
    brokerage_name: string | null;
    broker_name: string | null;
    /**
     * The invoiced policy number, including any correction (backend PR
     * #415, OR-1657). Null for invoices created before corrections shipped,
     * so the form falls back to Policy Details in that case.
     */
    policy_number: string | null;
    /**
     * The billed premium, including any correction. Since backend PR #415
     * this is the invoice's own snapshot, not the live quote: editing the
     * quote after invoicing no longer changes it.
     */
    selected_premium: string | null;
    /** The invoiced commission rates as fractions, at the 4 dp Ascend applies (0.1084 = 10.84%). */
    tricura_commission_rate: string | null;
    agent_commission_rate: string | null;

    /* The money fields backend PR #383 added for this page. The four live
     * ones are recomputed per read off the quote; the rest are stored. */

    /** Wholesaler / MGA fee: tricura_commission_rate × selected_premium. */
    tricura_commission_amount: string | null;
    /** Brokerage commission: agent_commission_rate × selected_premium. */
    agent_commission_amount: string | null;
    /** surplus_lines_tax_amount + stamping_fee_amount. */
    taxes_total: string | null;
    /**
     * indiana_pcf_surcharge + louisiana_pcf_surcharge ONLY.
     *
     * ⚠️ Narrower than what Ascend actually bills: TRIA, the surplus lines
     * FEE, MCARE and misc fees are folded into Ascend's `other_fees_cents`
     * but land in neither this nor `taxes_total`, so the card's parts can
     * sum to less than `total_invoiced_amount`. Raised with backend — do
     * not "fix" it by summing the quote here; display what it sends.
     */
    surcharges_total: string | null;
    /** Manual input, stored on the row. No Ascend counterpart. */
    tricura_commission_tax_amount: string | null;
    /** Manual input. Maps to Ascend's `agency_fees_cents` once a Billable exists. */
    agency_fee_amount: string | null;
    /** Sum of the four commission figures. Persisted exactly as the FE sends it. */
    total_commission_amount: string | null;
    /** Dollar counterpart of `ascend_total_amount_cents`; null until complete. */
    total_invoiced_amount: string | null;
    /** Ascend dashboard deep link. Null until the sync creates a Program. */
    ascend_program_url: string | null;
    /** Archive timestamp, or null. There is no un-archive endpoint today. */
    invoice_archived_at: string | null;
}

interface AscendInvoiceListWire {
    total: number;
    items: AscendInvoiceWire[];
}

/** FE model for one Invoice History row. */
export interface AscendInvoice {
    id: string;
    submissionId: string;
    syncStatus: AscendSyncStatus;
    invoiceSequence: number;
    invoiceNumber: string;
    /** ISO date (`YYYY-MM-DD`), or null until Ascend assigns one. */
    dueDate: string | null;
    createdAt: string;
    updatedAt: string;
    coverageType: AscendCoverageType;
    /** Snapshot of Ascend's billing status. See the wire doc — not live. */
    ascendInvoiceStatus: string | null;
    /** Dollars, converted from the wire's cents. */
    dueValue: number | null;
    namedInsured: string;
    effectiveDate: string | null;
    expirationDate: string | null;
    brokerageName: string;
    brokerName: string;
    /** Invoiced policy number; null for rows older than the correction flow. */
    policyNumber: string | null;
    /** Billed premium (the invoice's own snapshot, not the live quote). */
    selectedPremium: number | null;
    /** Invoiced commission rates as fractions (0.1 = 10%), or null. */
    tricuraCommissionRate: number | null;
    agentCommissionRate: number | null;
    /** See the wire doc for what each of these is and is not. */
    tricuraCommissionAmount: number | null;
    agentCommissionAmount: number | null;
    taxesTotal: number | null;
    surchargesTotal: number | null;
    tricuraCommissionTaxAmount: number | null;
    agencyFeeAmount: number | null;
    totalCommissionAmount: number | null;
    totalInvoicedAmount: number | null;
    ascendProgramUrl: string | null;
    archivedAt: string | null;
}

/** Decimal-as-string → number, tolerating null and unparseable input. */
function toAmount(raw: string | null): number | null {
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

export function toAscendInvoice(w: AscendInvoiceWire): AscendInvoice {
    return {
        id: w.id,
        submissionId: w.submission_id,
        syncStatus: w.sync_status,
        invoiceSequence: w.invoice_sequence,
        invoiceNumber: w.invoice_number,
        dueDate: w.due_date,
        createdAt: w.created_at,
        updatedAt: w.updated_at,
        coverageType: w.coverage_type,
        ascendInvoiceStatus: w.ascend_invoice_status,
        // 0 means "Ascend hasn't computed it yet", not a free invoice: the
        // backend snapshots the total before Ascend finishes deriving it and
        // re-reads it only when the payment-proposal PDF generates (PR #379).
        // A real $0 can't happen — Generate is blocked on premium > $0 — so
        // render the wait as a dash, never as $0.00.
        dueValue: w.ascend_total_amount_cents ? w.ascend_total_amount_cents / 100 : null,
        namedInsured: w.fni ?? "",
        effectiveDate: w.effective_date,
        expirationDate: w.expiration_date,
        brokerageName: w.brokerage_name ?? "",
        brokerName: w.broker_name ?? "",
        policyNumber: w.policy_number ?? null,
        selectedPremium: toAmount(w.selected_premium),
        tricuraCommissionRate: toAmount(w.tricura_commission_rate),
        agentCommissionRate: toAmount(w.agent_commission_rate),
        tricuraCommissionAmount: toAmount(w.tricura_commission_amount),
        agentCommissionAmount: toAmount(w.agent_commission_amount),
        taxesTotal: toAmount(w.taxes_total),
        surchargesTotal: toAmount(w.surcharges_total),
        tricuraCommissionTaxAmount: toAmount(w.tricura_commission_tax_amount),
        agencyFeeAmount: toAmount(w.agency_fee_amount),
        totalCommissionAmount: toAmount(w.total_commission_amount),
        totalInvoicedAmount: toAmount(w.total_invoiced_amount),
        ascendProgramUrl: w.ascend_program_url,
        archivedAt: w.invoice_archived_at,
    };
}

export interface ListAscendInvoicesParams {
    offset?: number;
    limit?: number;
}

export interface AscendInvoiceList {
    total: number;
    items: AscendInvoice[];
}

/** The endpoint's ceiling (`LimitQuery` is `ge=1, le=200`). */
const PAGE_LIMIT = 200;

/**
 * Every invoice for this submission, following the pagination to the end.
 *
 * The design has no pager — volume per submission is low — but the route
 * is still paginated, so asking for one page and ignoring `total` would
 * silently drop rows once a submission crosses the limit, and would make
 * the client-side search lie about what it searched. Same `fetchAllPages`
 * shape Loss Runs uses.
 */
export async function listAllAscendInvoices(submissionId: string): Promise<AscendInvoiceList> {
    const items: AscendInvoice[] = [];
    let total = 0;
    let offset = 0;
    for (;;) {
        const page = await listAscendInvoices(submissionId, { offset, limit: PAGE_LIMIT });
        total = page.total;
        items.push(...page.items);
        if (items.length >= page.total || page.items.length === 0) break;
        offset += PAGE_LIMIT;
    }
    return { total, items };
}

/**
 * One page of this submission's invoices, most recent first — including
 * failed attempts. Prefer `listAllAscendInvoices` unless you genuinely
 * want a single page. The endpoint has no search parameter (it doesn't
 * take the shared `SearchQuery` other list routes do), so Invoice History
 * filters client-side.
 */
export function listAscendInvoices(
    submissionId: string,
    params: ListAscendInvoicesParams = {},
): Promise<AscendInvoiceList> {
    const qs = new URLSearchParams({ submission_id: submissionId });
    if (params.offset != null) qs.set("offset", String(params.offset));
    if (params.limit != null) qs.set("limit", String(params.limit));
    return getJsonReal<AscendInvoiceListWire>(`/v2/invoices/?${qs}`).then((res) => ({
        total: res.total,
        items: res.items.map(toAscendInvoice),
    }));
}

// ─────────────────────── Ascend Account payloads ──────────────────────────

/** Ascend requires the agency address split into these four parts. */
export interface AccountMailingAddress {
    street_one: string;
    city: string;
    /** Two-letter state code. */
    state: string;
    zip_code: string;
}

export interface CreateAccountRequest {
    organization_account: {
        contact: { first_name: string; last_name: string; email: string };
        agency: { name: string; phone: string; mailing_address: AccountMailingAddress };
        /** Decimal rate, e.g. `0.125` for 12.5%. */
        default_seller_commission_rate: number;
        /** Orion never uses direct billing — always `agency`. */
        billing_type: "agency";
    };
}

export interface CreateAccountUserRequest {
    email: string;
    first_name: string;
    last_name?: string | null;
    phone?: string | null;
    role?: "producer";
}

/**
 * Body for the create call. Account resolution is bundled in here — there
 * is no separate Account route.
 *
 * `account_payload` and `link_account_id` are MUTUALLY EXCLUSIVE: create a
 * new Ascend Account or link an existing one, never both. Send neither once
 * the brokerage already has a confirmed link.
 */
export interface CreateAscendInvoiceRequest {
    submission_id: string;
    coverage_type: AscendCoverageType;
    account_payload?: CreateAccountRequest | null;
    account_user_payload?: CreateAccountUserRequest | null;
    link_account_id?: string | null;
}

/**
 * Create the invoice and queue its sync to Ascend. Resolves with a
 * `pending` row — poll `sync_status` (see `src/state/ascend-invoice-status.ts`)
 * to learn whether Ascend actually accepted it.
 *
 * Throws `ApiError`:
 *  - **409** the submission already has a live invoice with different
 *    details. An identical repeat instead returns the existing invoice, so
 *    a retry after a network blip is safe.
 *  - **422** a missing precondition: no linked broker or brokerage, missing
 *    effective/expiration dates, no policy to render the binder from, or an
 *    `account_payload` that is required but absent. `error.message` carries
 *    the backend's explanation and is safe to show.
 */
export function createAscendInvoice(body: CreateAscendInvoiceRequest): Promise<AscendInvoice> {
    return sendJsonReal<AscendInvoiceWire>("/v2/invoices/", "POST", body).then(toAscendInvoice);
}

const SYNC_TERMINAL: ReadonlySet<AscendSyncStatus> = new Set(["complete", "failed"]);

/**
 * Poll a just-created invoice until its Ascend sync settles, or the
 * timeout elapses. Resolves with the final row, or `"timeout"` if it never
 * settles — the caller decides whether that reads as an error (it usually
 * doesn't: the worker keeps going and the row will be correct on reload).
 *
 * Re-lists the submission's invoices and picks the row by id, because the
 * backend exposes **no single-invoice GET**. Cheap enough at this volume,
 * but it is the reason this takes a `submissionId` it shouldn't need.
 *
 * Transient GET failures are swallowed and retried to the deadline — a
 * network blip mustn't strand the user on a spinner after the invoice was
 * already created. Runs from the submit handler, not render, so it is a
 * plain async loop rather than a hook.
 */
export async function waitForAscendInvoiceSync(
    submissionId: string,
    invoiceId: string,
    opts: { intervalMs?: number; timeoutMs?: number; onPoll?: (invoice: AscendInvoice) => void } = {},
): Promise<AscendInvoice | "timeout"> {
    const intervalMs = opts.intervalMs ?? 3000;
    const timeoutMs = opts.timeoutMs ?? 120_000;
    const deadline = Date.now() + timeoutMs;
    for (;;) {
        try {
            const { items } = await listAscendInvoices(submissionId, { limit: 50 });
            const row = items.find((i) => i.id === invoiceId);
            if (row) {
                opts.onPoll?.(row);
                if (SYNC_TERMINAL.has(row.syncStatus)) return row;
            }
        } catch {
            // Transient — ignore and retry until the deadline.
        }
        if (Date.now() >= deadline) return "timeout";
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
}

// ───────────────────────────── the draft ──────────────────────────────────

/**
 * Body for the draft upsert. Deliberately tiny, mirroring the backend:
 * only the coverage line and the Account choice are persisted — every
 * other field on the form is re-derived live from the submission on each
 * read, exactly like the create flow.
 */
export interface AscendInvoiceDraftPut {
    coverage_type?: AscendCoverageType | null;
    account_payload?: CreateAccountRequest | null;
    account_user_payload?: CreateAccountUserRequest | null;
    link_account_id?: string | null;
    /**
     * The three money fields a draft carries. Decimal strings, as the API
     * takes them. They survive `finalize_draft()`, which flips the draft
     * row to `pending` in place without touching these columns — so what
     * the user saved here is what the generated invoice bills.
     */
    tricura_commission_tax_amount?: string | null;
    agency_fee_amount?: string | null;
    total_commission_amount?: string | null;
}

/** Body of `PATCH /v2/invoices/{id}` — the post-draft money correction. */
export interface AscendInvoiceUpdate {
    tricura_commission_tax_amount?: string | null;
    agency_fee_amount?: string | null;
    total_commission_amount?: string | null;
}

/** What `updateAscendInvoice` resolves to: the corrected invoice as the backend returns it. */
export interface AscendInvoiceUpdateResult {
    invoice: AscendInvoice;
}

export interface AscendInvoiceDraft {
    id: string;
    submissionId: string;
    coverageType: AscendCoverageType | null;
    linkAccountId: string | null;
    accountPayload: CreateAccountRequest | null;
    accountUserPayload: CreateAccountUserRequest | null;
    createdAt: string;
    /** The three money fields a draft persists (backend PR #383). */
    tricuraCommissionTaxAmount: number | null;
    agencyFeeAmount: number | null;
    totalCommissionAmount: number | null;
}

interface AscendInvoiceDraftWire {
    id: string;
    submission_id: string;
    coverage_type: AscendCoverageType | null;
    link_account_id: string | null;
    account_payload: CreateAccountRequest | null;
    account_user_payload: CreateAccountUserRequest | null;
    created_at: string;
    tricura_commission_tax_amount: string | null;
    agency_fee_amount: string | null;
    total_commission_amount: string | null;
}

function toDraft(w: AscendInvoiceDraftWire): AscendInvoiceDraft {
    return {
        id: w.id,
        submissionId: w.submission_id,
        coverageType: w.coverage_type,
        linkAccountId: w.link_account_id,
        accountPayload: w.account_payload,
        accountUserPayload: w.account_user_payload,
        createdAt: w.created_at,
        tricuraCommissionTaxAmount: toAmount(w.tricura_commission_tax_amount),
        agencyFeeAmount: toAmount(w.agency_fee_amount),
        totalCommissionAmount: toAmount(w.total_commission_amount),
    };
}

/** Save (upsert) the in-progress form. */
export function putInvoiceDraft(submissionId: string, body: AscendInvoiceDraftPut): Promise<AscendInvoiceDraft> {
    return sendJsonReal<AscendInvoiceDraftWire>(draftBase(submissionId), "PUT", body).then(toDraft);
}

/** The saved draft, or null — the backend answers 404 when there is none. */
export async function getInvoiceDraft(submissionId: string): Promise<AscendInvoiceDraft | null> {
    try {
        return toDraft(await getJsonReal<AscendInvoiceDraftWire>(draftBase(submissionId)));
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
    }
}

/** Discard the draft. Tolerates there being none (404). */
export async function deleteInvoiceDraft(submissionId: string): Promise<void> {
    try {
        await sendJsonReal<void>(draftBase(submissionId), "DELETE");
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) return;
        throw error;
    }
}

/**
 * Edit a finalized invoice's money fields (`PATCH /v2/invoices/{id}`).
 *
 * Only the three writable fields; everything else on an invoice is derived
 * or frozen. Throws `ApiError` 409 when the invoice is archived or a
 * correction is already in progress, 404 when it doesn't exist, 422 when
 * the invoice is not complete yet.
 */
export async function updateAscendInvoice(
    invoiceId: string,
    body: AscendInvoiceUpdate,
): Promise<AscendInvoiceUpdateResult> {
    // The backend answers with the corrected invoice itself (no wrapper):
    // every failure mode is a real HTTP status (404 / 409 / 422 / 502) and
    // surfaces as `ApiError`.
    const wire = await sendJsonReal<AscendInvoiceWire>(`/v2/invoices/${encodeURIComponent(invoiceId)}`, "PATCH", body);
    return { invoice: toAscendInvoice(wire) };
}

/**
 * Archive an invoice (`DELETE /v2/invoices/{id}`), backend PR #383.
 *
 * Synchronous and soft: the row keeps being listed with
 * `invoice_archived_at` set, which is what the struck-through style in the
 * history table reads. The backend branches per `sync_status` and archives
 * the Ascend Program too when the invoice holds one.
 *
 * There is NO un-archive endpoint, so the design's Restore has nothing to
 * call — it stays disabled until one exists.
 *
 * Throws `ApiError` 409 when the invoice is mid-sync (`running`); the
 * caller should surface that rather than retrying.
 */
export function archiveAscendInvoice(invoiceId: string): Promise<void> {
    return sendJsonReal<void>(`/v2/invoices/${encodeURIComponent(invoiceId)}`, "DELETE");
}

// ─────────────────────── The generated document ───────────────────────────

interface InvoiceFileWire {
    id: string;
    name: string;
    content_type: string | null;
    size_bytes: number | null;
    uploaded_at: string | null;
    download_url: string | null;
}

interface InvoiceFileListWire {
    total: number;
    items: InvoiceFileWire[];
}

export interface InvoiceFile {
    id: string;
    name: string;
    contentType: string;
    sizeBytes: number;
    uploadedAt: string | null;
    /** Presigned GET URL — empty string when the backend withheld one. */
    downloadUrl: string;
}

/**
 * Files archived against the invoice — in practice the generated invoice
 * PDF, written by the document worker after the sync completes. Empty
 * until then, which is why Download stays disabled on a pending row.
 *
 * Trailing slash required (collection path).
 */
export function listAscendInvoiceFiles(invoiceId: string): Promise<InvoiceFile[]> {
    return getJsonReal<InvoiceFileListWire>(`/v2/invoices/${encodeURIComponent(invoiceId)}/files/`).then(
        (res) =>
            res.items.map((w) => ({
                id: w.id,
                name: w.name,
                contentType: w.content_type ?? "",
                sizeBytes: w.size_bytes ?? 0,
                uploadedAt: w.uploaded_at,
                downloadUrl: w.download_url ?? "",
            })),
    );
}
